import React, { useEffect, useState } from "react";
import styles from "./Header.module.scss";
import LogoWhite from "../../icons/borderly-plus-white-logo.svg";
import MenuIcon from "../../icons/menu.svg";
import RefreshIcon from "../../icons/refresh.svg";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { updateMenuActive } from "../../redux/layoutStore";
import { useNavigate, useParams } from "react-router-dom";
import { showModal } from "../../redux/modalStore";
import Button from "../../components/Button/Button";
import { getOrganizationData } from "../../actions/organization";
import { setLoadingContext } from "../../redux/loaderStore";
import { REFRESH_BUTTON_LOADER } from "../../constants/loader";
import axiosInstance from "../../utils/utils";
import { setUserData } from "../../redux/userStore";
import { resolveInsightsOrigin } from "../../utils/auth";

const REFRESH_INTERVAL = 12 * 60 * 60 * 1000;

const setSharedCookie = (name, val) => {
  const isLocal = window.location.hostname === "localhost";
  const cookieDomain = isLocal ? "" : "domain=.border.ly;";
  document.cookie = `${name}=${val}; ${cookieDomain} path=/; secure; samesite=None`;
};

const Header = ({ menuIconRef }) => {
  const menuActive = useSelector((state) => state.layout.menuActive);
  const user = useSelector((state) => state.user.user);

  const [backgroundColor, setBackgroundColor] = useState(null);
  const [hasTransparentBackground, setHasTransparentBackground] =
    useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isLogoError, setIsLogoError] = useState(false);

  const { orgId: orgIdFromParams } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sessionToken = localStorage.getItem("session_token");

  const resolvedOrgId = orgIdFromParams || localStorage.getItem("org_id") || "";

  const modules = useSelector((state) => state.user.modules[orgIdFromParams]);
  const currentOrganization = useSelector(
    (state) => state.user.organizations[orgIdFromParams],
  );

  const logoUrl = currentOrganization?.Logo?.url;

  useEffect(() => {
    if (!orgIdFromParams) return;
    localStorage.setItem("org_id", orgIdFromParams);
    setSharedCookie("org_id", orgIdFromParams);
  }, [orgIdFromParams]);

  // Function to check if background from organization logo is transparent and if not, find background color;
  useEffect(() => {
    if (logoUrl) {
      const img = new Image();
      img.src = logoUrl;
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        let transparentPixelCount = 0;
        let opaquePixelCount = 0;
        const colorCount = {};
        let maxCount = 0;
        let dominantColor = null;

        for (let i = 0; i < pixels.length; i += 4) {
          const alpha = pixels[i + 3];

          if (alpha < 255) {
            transparentPixelCount++;
          } else {
            opaquePixelCount++;
            const color = `${pixels[i]},${pixels[i + 1]},${pixels[i + 2]}`;
            colorCount[color] = (colorCount[color] || 0) + 1;

            if (colorCount[color] > maxCount) {
              maxCount = colorCount[color];
              dominantColor = color;
            }
          }
        }

        const totalPixels = transparentPixelCount + opaquePixelCount;
        const transparencyRatio = transparentPixelCount / totalPixels;

        const transparencyThreshold = 0.1;

        setHasTransparentBackground(transparencyRatio > transparencyThreshold);
        setBackgroundColor(
          transparencyRatio > transparencyThreshold
            ? "#FFFFFF"
            : `rgb(${dominantColor})`,
        );
        setImageLoaded(true);
        setIsLogoError(false);
      };

      img.onerror = () => {
        console.error("Failed to load logo image");
        setIsLogoError(true);
      };
    }
  }, [logoUrl]);

  const handleOpenInsights = () => {
    if (resolvedOrgId) setSharedCookie("org_id", resolvedOrgId);

    const base = resolveInsightsOrigin(); // localhost:3000 -> localhost:5173, border.ly -> insights.border.ly
    const target = resolvedOrgId
      ? `${base}/${encodeURIComponent(resolvedOrgId)}`
      : `${base}/organizations`;
    window.location.assign(target);
  };

  const handleOpenOrganizationsSwitchModal = () => {
    dispatch(
      showModal({
        name: "organizations_switch_modal",
      }),
    );
  };

  const toggleMenuActive = () => {
    dispatch(updateMenuActive(!menuActive));
  };

  const handleHomepageNavigation = () => {
    navigate(orgIdFromParams ? `/${orgIdFromParams}` : "/organizations");
  };

  useEffect(() => {
    const checkAndRefreshData = () => {
      const lastRefresh = localStorage.getItem("last_refresh_timestamp");
      const now = Date.now();

      if (!lastRefresh || now - lastRefresh > REFRESH_INTERVAL) {
        handleRefreshData();
        localStorage.setItem("last_refresh_timestamp", now);
      }
    };

    // Run the check on component mount
    checkAndRefreshData();

    // Set an interval to run the check every 12 hours
    const interval = setInterval(checkAndRefreshData, REFRESH_INTERVAL);

    // Clear the interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const handleRefreshData = () => {
    if (!orgIdFromParams) return;

    dispatch(
      setLoadingContext({
        context: REFRESH_BUTTON_LOADER,
        isLoading: true,
      }),
    );

    dispatch(
      getOrganizationData(orgIdFromParams, sessionToken, navigate, false),
    )
      .then(() => {
        const refreshQuery = "&refresh=true";
        const options = {
          method: "GET",
          url: `/auth/session?session_token=${sessionToken}${refreshQuery}`,
        };

        return axiosInstance
          .request(options)
          .then((response) => {
            dispatch(setUserData(response.data));
            localStorage.setItem("last_refresh_timestamp", Date.now());
          })
          .catch(() => {
            dispatch(showModal({ name: "error_modal" }));
          });
      })
      .finally(() => {
        dispatch(
          setLoadingContext({
            context: REFRESH_BUTTON_LOADER,
            isLoading: false,
          }),
        );
      });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.leftSide}>
        <img
          src={LogoWhite}
          alt="Borderly logo"
          className={styles.logo}
          onClick={handleHomepageNavigation}
        />
        {logoUrl && !isLogoError && (
          <>
            <div
              className={styles.imageWrapper}
              style={{
                backgroundColor: hasTransparentBackground
                  ? "#FFFFFF"
                  : backgroundColor,
              }}
            >
              {imageLoaded && (
                <img src={logoUrl} alt="Logo" className={styles.orgLogo} />
              )}
            </div>
          </>
        )}
        {user?.Role === "Consultant" && (
          <Button
            text="Customs insights"
            className={styles.insights}
            onClick={handleOpenInsights}
          />
        )}
      </div>
      <div className={styles.rightSide}>
        <Button
          text="Refresh data"
          icon={RefreshIcon}
          className={styles.refresh}
          onClick={handleRefreshData}
        />
        <div
          className={styles.orgName}
          onClick={handleOpenOrganizationsSwitchModal}
        >
          {currentOrganization?.Name}
        </div>
        {modules?.length > 0 && (
          <img
            ref={menuIconRef}
            src={MenuIcon}
            alt="Menu icon"
            className={classNames(styles.icon, menuActive && styles.active)}
            onClick={toggleMenuActive}
          />
        )}
      </div>
    </div>
  );
};

export default Header;
